import { motion } from "framer-motion";
import { EXTERNAL_LINKS } from "../constants/externalLinks";

export default function FindUs() {
  return (
    <section
      style={{
        background: "#fffdf8",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Top wave — blends from teal WeeklySaturdayMarket */}
      <div style={{ lineHeight: 0 }}>
        <svg
          viewBox="0 0 1440 60"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          style={{ width: "100%", display: "block", height: "60px" }}
        >
          <path
            d="M0 0V28C240 55 480 62 720 38C960 14 1200 32 1440 28V0H0Z"
            fill="#1a4209"
          />
        </svg>
      </div>

      <div
        style={{
          maxWidth: "1160px",
          margin: "0 auto",
          padding: "clamp(40px, 6vw, 72px) clamp(20px, 4vw, 40px) clamp(48px, 7vw, 88px)",
        }}
      >
        <div className="fu-layout">
          {/* Left — Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}
          >
            {/* Badge */}
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "7px",
                background: "rgba(198,37,45,0.08)",
                border: "1px solid rgba(198,37,45,0.2)",
                borderRadius: "999px",
                padding: "8px 20px",
                color: "#c6252d",
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 700,
                fontSize: "0.76rem",
                letterSpacing: "2px",
                textTransform: "uppercase",
                marginBottom: "22px",
                alignSelf: "flex-start",
              }}
            >
              📍 Find Us
            </span>

            {/* Address */}
            <h2
              style={{
                fontFamily: "'Montserrat', 'Segoe UI', sans-serif",
                fontWeight: 900,
                fontSize: "clamp(2rem, 5vw, 3.2rem)",
                letterSpacing: "-1.5px",
                lineHeight: 1.1,
                margin: "0 0 10px",
                color: "#0d1b2a",
              }}
            >
              245 Freshwater Rd
            </h2>
            <p
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "clamp(1rem, 2.2vw, 1.25rem)",
                color: "#5a6a7a",
                fontWeight: 500,
                margin: "0 0 28px",
              }}
            >
              St. John's, NL &nbsp;A1B 1B3
            </p>

            {/* Info pills */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "32px" }}>
              {[
                { icon: "🕘", text: "9:00 AM – 4:00 PM" },
                { icon: "📅", text: "Every Saturday" },
                { icon: "☀️", text: "Rain or Shine" },
              ].map((pill) => (
                <span
                  key={pill.text}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    background: "#f4f8fb",
                    border: "1px solid #e0eaf2",
                    borderRadius: "999px",
                    padding: "8px 16px",
                    fontSize: "0.86rem",
                    fontWeight: 600,
                    color: "#2d6a14",
                    fontFamily: "'Montserrat', sans-serif",
                  }}
                >
                  {pill.icon} {pill.text}
                </span>
              ))}
            </div>

            {/* CTA */}
            <a
              href={EXTERNAL_LINKS.marketGoogleMap}
              target="_blank"
              rel="noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "10px",
                background: "linear-gradient(90deg, #c6252d, #e84545)",
                color: "#fff",
                padding: "0.9em 2.4em",
                borderRadius: "999px",
                textDecoration: "none",
                fontWeight: 700,
                fontSize: "1rem",
                fontFamily: "'Montserrat', sans-serif",
                letterSpacing: "0.3px",
                boxShadow: "0 6px 24px rgba(198,37,45,0.3)",
                alignSelf: "flex-start",
                transition: "transform 0.2s, box-shadow 0.2s",
              }}
              className="fu-directions-btn"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M9 1.5C6.1 1.5 3.75 3.85 3.75 6.75C3.75 10.69 9 16.5 9 16.5C9 16.5 14.25 10.69 14.25 6.75C14.25 3.85 11.9 1.5 9 1.5ZM9 8.625C7.97 8.625 7.125 7.78 7.125 6.75C7.125 5.72 7.97 4.875 9 4.875C10.03 4.875 10.875 5.72 10.875 6.75C10.875 7.78 10.03 8.625 9 8.625Z" fill="white"/>
              </svg>
              Get Directions
            </a>
          </motion.div>

          {/* Right — Map */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, ease: "easeOut", delay: 0.15 }}
            style={{
              borderRadius: "24px",
              overflow: "hidden",
              boxShadow: "0 16px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)",
              minHeight: "clamp(260px, 40vw, 420px)",
              position: "relative",
            }}
          >
            {/* Decorative corner accent */}
            <div
              style={{
                position: "absolute",
                top: 0, left: 0, right: 0,
                height: "4px",
                background: "linear-gradient(90deg, #c6252d, #6dc744)",
                zIndex: 2,
              }}
            />
            <iframe
              title="St. John's Farmers' Market Location"
              src="https://www.google.com/maps?q=245+Freshwater+Rd+St.+John's,+NL+A1B+1B3&output=embed"
              width="100%"
              height="100%"
              style={{
                border: 0,
                display: "block",
                minHeight: "clamp(260px, 40vw, 420px)",
              }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </motion.div>
        </div>
      </div>

      <style>{`
        .fu-layout {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: clamp(28px, 5vw, 64px);
          align-items: center;
        }
        .fu-directions-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 32px rgba(198,37,45,0.45) !important;
        }
        @media (max-width: 768px) {
          .fu-layout {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </section>
  );
}
